/**
 * Per-session presence identity for the article editor.
 *
 * Three internal users share ONE Cognito account, so the user identity
 * (`sub`/email) can't tell them apart. We instead tag presence with a per-TAB
 * session id and a self-chosen display name (persisted in localStorage). The
 * websocket server relays the awareness payload untouched, so distinguishing the
 * sessions is purely a client-side convention. See docs/hermes-review-awareness.md.
 */

const DISPLAY_NAME_KEY = "articlePresenceName";

// One id per browser tab — a module singleton, deliberately NOT persisted, so
// two tabs are two distinct sessions (which is the behaviour we want).
let sessionId: string | null = null;

export const getSessionId = (): string => {
  if (!sessionId) {
    const rand = Math.random().toString(36).slice(2, 10);
    sessionId = `sess_${Date.now().toString(36)}_${rand}`;
  }
  return sessionId;
};

const hashString = (key: string, seed = 0): number => {
  let hash = seed;
  for (let i = 0; i < key.length; i++) {
    hash = (hash << 5) - hash + key.charCodeAt(i);
    hash = hash & hash;
  }
  return Math.abs(hash);
};

// Friendly, distinct default alias per session so the three shared-account
// users are immediately distinguishable without anyone having to rename.
const ALIAS_ADJECTIVES = [
  "Swift", "Calm", "Brave", "Bright", "Bold", "Clever", "Quiet", "Witty",
  "Keen", "Sunny", "Lucky", "Mellow", "Nimble", "Jolly", "Spry", "Cosmic",
];
const ALIAS_ANIMALS = [
  "Otter", "Falcon", "Panda", "Fox", "Heron", "Lynx", "Koala", "Wren",
  "Tapir", "Bison", "Gecko", "Ibis", "Marten", "Quokka", "Stoat", "Robin",
];

export const aliasForSession = (key: string): string => {
  const adj = ALIAS_ADJECTIVES[hashString(key, 7) % ALIAS_ADJECTIVES.length];
  const animal = ALIAS_ANIMALS[hashString(key, 131) % ALIAS_ANIMALS.length];
  return `${adj} ${animal}`;
};

/**
 * The user's chosen display name if they set one, else a stable per-session
 * alias (NOT the shared Cognito identity — that would make every session look
 * identical).
 */
export const getDisplayName = (): string => {
  if (typeof window !== "undefined") {
    const stored = window.localStorage.getItem(DISPLAY_NAME_KEY);
    if (stored && stored.trim()) return stored.trim();
  }
  return aliasForSession(getSessionId());
};

export const hasChosenDisplayName = (): boolean => {
  if (typeof window === "undefined") return false;
  const stored = window.localStorage.getItem(DISPLAY_NAME_KEY);
  return !!(stored && stored.trim());
};

export const setStoredDisplayName = (name: string): void => {
  if (typeof window === "undefined") return;
  const trimmed = name.trim();
  if (trimmed) window.localStorage.setItem(DISPLAY_NAME_KEY, trimmed);
  else window.localStorage.removeItem(DISPLAY_NAME_KEY);
};

// Stable, distinct color per key. Palette mirrors useYjsDocument's
// generateUserColor so presence colors feel consistent across the app.
const PRESENCE_COLORS = [
  "#F44336", "#E91E63", "#9C27B0", "#673AB7",
  "#3F51B5", "#2196F3", "#03A9F4", "#00BCD4",
  "#009688", "#4CAF50", "#8BC34A", "#CDDC39",
  "#FFC107", "#FF9800", "#FF5722",
];

export const colorForSession = (key: string): string => {
  let hash = 0;
  for (let i = 0; i < key.length; i++) {
    hash = (hash << 5) - hash + key.charCodeAt(i);
    hash = hash & hash;
  }
  return PRESENCE_COLORS[Math.abs(hash) % PRESENCE_COLORS.length];
};

/** Up-to-two-letter initials for an avatar. */
export const initials = (name: string): string => {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};
